import React, { useState, useRef } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { Field, Formik } from 'formik';
import 'bootstrap/dist/css/bootstrap.css';
import Select from 'react-select';
import { FaSpinner } from 'react-icons/fa';
import { Country, State, City } from 'country-state-city';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';

import Wrapper from '../../components/Wrapper';
import clearCookies from '../../utils/clearCookies';
import axiosInstance from '../../utils/axios';

// for form validation
const schema = Yup.object({
  name: Yup.string().required('name is required'),
  
  mobile: Yup.string()
    .required('Mobile is required')
    .test('is-valid-phone', 'Invalid Phone number', (value) => 
      value ? isValidPhoneNumber(value) : false
    ),
    
  email: Yup.string().required('Email is required'),
  address: Yup.string().required('Address is required'),
  pincode: Yup.string()
      .required('Zip/Postal Code is required')
      .matches(/^[a-zA-Z0-9\s\-]{3,12}$/, 'Invalid Zip/Postal Code'),
  country: Yup.string().required('Country is required'),
  state: Yup.string().required('State is required'),
  city: Yup.string().required('City is required'),
});

function generateClassName(errors, touched, fieldName) {
  const hasError = errors[fieldName] && touched[fieldName];
  const isSuccess = touched[fieldName];
  return `form-control ${hasError
    ? 'border-2 border-danger'
    : isSuccess
      ? 'border-2 border-success'
      : ''
    }`;
}

const generatePhoneClassName = (errors, touched, fieldName) => {
  const hasError = errors[fieldName] && touched[fieldName];
  const isSuccess = touched[fieldName];
  return `d-flex align-items-center form-control ${
    hasError ? 'border-2 border-danger' : isSuccess ? 'border-2 border-success' : ''
  }`;
};

const AddFieldOfficer = () => {
  const citySelectRef = useRef(null);
  const stateSelectRef = useRef(null);
  const [countryCode, setCountryCode] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const updatedCountries = () =>
    Country.getAllCountries().map((country) => ({
      label: country.name,
      value: country.isoCode,
      ...country,
    }));

  const updatedStates = (isoCountryCode) =>
    isoCountryCode ? State.getStatesOfCountry(isoCountryCode).map((state) => ({
      key: state.id,
      label: state.name,
      value: state.id,
      ...state,
    })) : [];

  const updatedCities = (isoCountryCode, isoStateCode) =>
    (isoCountryCode && isoStateCode) ? City.getCitiesOfState(isoCountryCode, isoStateCode).map((city) => ({
      label: city.name,
      value: city.id,
      ...city,
    })) : [];

  return (
    <Wrapper>
      <Formik
        initialValues={{
          name: '',
          mobile: '',
          email: '',
          address: '',
          pincode: '',
          country: '',
          state: '',
          city: '',
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          setIsLoading(true);

          let formattedMobile = values.mobile;
          if (formattedMobile && formattedMobile.startsWith('+91')) {
            formattedMobile = formattedMobile.replace('+91', '');
          }

          const data = {
            name: values.name,
            mobile_no: formattedMobile,
            email_id: values.email,
            address: values.address,
            pincode: values.pincode,
            city: values.city,
            state: values.state,
          };

          axiosInstance
            .post(`/admin/add-field-officer`, {
              ...data,
            })
            .then(({ data }) => {
              navigate('/field-officers');
            })
            .catch((error) => {
              console.error("Error submitting form:", error);

              if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                clearCookies();
                window.location.href = '/';
              } else {
                const errorMsg = error.response?.data?.message || "Something went wrong. Please try again.";
                alert(errorMsg);
              }
            })
            .finally(() => {
              setIsLoading(false);
            });
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
        }) => (
          <div className='mt-5 container-md'>
            <div className='d-flex justify-content-center'>
              <div className='' contentEditable={false}>
                <Form
                  className='form__details'
                  onSubmit={handleSubmit}
                >
                  <h3 className='pt-4 pb-0'>Add field officer</h3>
                  <span>
                    Please fill in the below details to add a field officer
                  </span>
                  <Row className='my-4'>
                    {/* name */}
                    <Form.Group as={Col} md='6' className='group mb-3'>
                      <div className='form-floating'>
                        <Field
                          type='text'
                          name='name'
                          value={values.name}
                          placeholder='Enter name'
                          className={generateClassName(errors, touched, 'name')}
                          onChange={handleChange}
                        />
                        <Form.Label htmlFor='name'>
                          Name<span className='text-danger'>&nbsp;*</span>
                        </Form.Label>
                      </div>
                      {errors.name && touched.name ? (
                        <div className='form_errors mt-1 text-danger small'>
                          &#9888; {errors.name}
                        </div>
                      ) : null}
                    </Form.Group>

                    {/* mobile */}
                    <Form.Group as={Col} md='6' className='group mb-3'>
                      <div>
                        <Field name="mobile">
                          {({ field, form }) => (
                            <PhoneInput
                              international
                              defaultCountry="IN"
                              placeholder="Enter Mobile *"
                              value={field.value}
                              onChange={(val) => {
                                form.setFieldValue(field.name, val);
                                form.setFieldTouched(field.name, true, false);
                              }}
                              onBlur={() => form.setFieldTouched(field.name, true)}
                              className={generatePhoneClassName(errors, touched, 'mobile')}
                            />
                          )}
                        </Field>
                      </div>
                      {errors.mobile && touched.mobile ? (
                        <div className='form_errors mt-1 text-danger small'>
                          &#9888; {errors.mobile}
                        </div>
                      ) : null}
                    </Form.Group>

                    {/* email */}
                    <Form.Group as={Col} md='6' className='group mb-3'>
                      <div className='form-floating'>
                        <Field
                          type='email'
                          name='email'
                          value={values.email}
                          placeholder='Enter Email'
                          className={generateClassName(errors, touched, 'email')}
                          onChange={handleChange}
                        />
                        <Form.Label htmlFor='email'>
                          Email
                          <span className='text-danger'>&nbsp;*</span>
                        </Form.Label>
                      </div>
                      {errors.email && touched.email ? (
                        <div className='form_errors mt-1 text-danger small'>
                          &#9888; {errors.email}
                        </div>
                      ) : null}
                    </Form.Group>

                    {/* address */}
                    <Form.Group as={Col} md='6' className='group mb-3'>
                      <div className='form-floating'>
                        <Field
                          type='text'
                          name='address'
                          value={values.address}
                          placeholder='Enter Address'
                          className={generateClassName(errors, touched, 'address')}
                          onChange={handleChange}
                        />
                        <Form.Label htmlFor='address'>
                          Address<span className='text-danger'>&nbsp;*</span>
                        </Form.Label>
                      </div>
                      {errors.address && touched.address ? (
                        <div className='form_errors mt-1 text-danger small'>
                          &#9888; {errors.address}
                        </div>
                      ) : null}
                    </Form.Group>

                    {/* pincode */}
                    <Form.Group as={Col} md='6' className='group mb-3'>
                      <div className='form-floating'>
                        <Field
                          type='text'
                          name='pincode'
                          value={values.pincode}
                          placeholder='Enter Pincode'
                          className={generateClassName(errors, touched, 'pincode')}
                          onChange={handleChange}
                        />
                        <Form.Label htmlFor='pincode'>
                          Zip / Postal Code<span className='text-danger'>&nbsp;*</span>
                        </Form.Label>
                      </div>
                      {errors.pincode && touched.pincode ? (
                        <div className='form_errors mt-1 text-danger small'>
                          &#9888; {errors.pincode}
                        </div>
                      ) : null}
                    </Form.Group>

                    {/* country */}
                    <Form.Group as={Col} md='6' className='group mb-3'>
                      <Select
                        name='country'
                        className={`custom-dropdown ${generateClassName(errors, touched, 'country')}`}
                        isSearchable={true}
                        placeholder='Select Country'
                        options={updatedCountries()}
                        onChange={(value) => {
                          setFieldValue('country', value ? value.label : '');
                          setCountryCode(value ? value.value : '');
                          
                          setFieldValue('state', '');
                          setStateCode('');
                          setFieldValue('city', '');
                          if (stateSelectRef.current) stateSelectRef.current.clearValue();
                          if (citySelectRef.current) citySelectRef.current.clearValue();
                        }}
                        onBlur={() => setFieldTouched('country', true)}
                      />
                      {errors.country && touched.country ? (
                        <div className='form_errors mt-1 text-danger small'>
                          &#9888; {errors.country}
                        </div>
                      ) : null}
                    </Form.Group>

                    {/* state */}
                    <Form.Group as={Col} md='6' className='group mb-3'>
                      <Select
                        id='state'
                        name='state'
                        ref={stateSelectRef}
                        className={`custom-dropdown ${generateClassName(errors, touched, 'state')}`}
                        defaultValue={values.state}
                        isSearchable={true}
                        isDisabled={!countryCode} 
                        placeholder='Select State'
                        options={updatedStates(countryCode)}
                        onChange={(value) => {
                          setFieldValue('state', value ? value.name : '');
                          setStateCode(value ? value.isoCode : '');
                          
                          // Clear city when state changes
                          setFieldValue('city', '');
                          if (citySelectRef.current) citySelectRef.current.clearValue();
                        }}
                        onBlur={() => setFieldTouched('state', true)}
                      />
                      {errors.state && touched.state ? (
                        <div className='form_errors mt-1 text-danger small'>
                          &#9888; {errors.state}
                        </div>
                      ) : null}
                    </Form.Group>

                    {/* city */}
                    <Form.Group as={Col} md='6' className='group mb-3'>
                      <Select
                        id='city'
                        name='city'
                        className={`custom-dropdown ${generateClassName(errors, touched, 'city')}`}
                        ref={citySelectRef}
                        isSearchable={true}
                        isDisabled={!stateCode}
                        placeholder='Select City'
                        options={updatedCities(
                          countryCode,
                          values.state && stateCode ? stateCode : null
                        )}
                        onChange={(value) => {
                          setFieldValue('city', value ? value.name : '');
                        }}
                        onBlur={() => setFieldTouched('city', true)}
                      />
                      {errors.city && touched.city ? (
                        <div className='form_errors mt-1 text-danger small'>
                          &#9888; {errors.city}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Row>

                  <div className='my-3 col col-md-6 mx-auto'>
                    {/* Submit button */}
                    <button
                      type='submit'
                      className='w-100 text-center btn btn-dark'
                      disabled={isLoading}
                    >
                      {isLoading && (
                        <Spinner
                          as='span'
                          animation='border'
                          size='sm'
                          role='status'
                          aria-hidden='true'
                          className="me-2"
                        />
                      )}
                      <span> Add</span>
                    </button>
                  </div>
                </Form>
              </div>
            </div>

            {isLoading && (
              <div className='loading-overlay'>
                <FaSpinner className='loading-icon spin' />
              </div>
            )}
          </div>
        )}
      </Formik>
    </Wrapper>
  );
};

export default AddFieldOfficer;